import { AnyAction, ListenerMiddlewareInstance, ThunkDispatch } from '@reduxjs/toolkit'
import { AppState } from '@redux/store'
import { authApi } from '@redux/queries/auth'
import { claimRecordsApi } from '@redux/queries/claimRecords'
import { connectorResetOnLoginEvent } from '@redux/slices/connectorSlice'
import { isGatewayTimeout } from '@utils/errorUtil'
import { getInsuranceToggleState } from '@utils/insuranceToggleUtil'
import { SyncInsuranceAction, SyncInsuranceListenerList } from '@typesApp/SyncListener'

type ListenerInstance = ListenerMiddlewareInstance<unknown, ThunkDispatch<unknown, unknown, AnyAction>, unknown>

/**
 * US flow - on login, invoke the sync action if customer has existing synced insurance
 */
export const initLoginSyncActionListener = (listenerMiddleware: ListenerInstance) => {
  listenerMiddleware.startListening({
    matcher: authApi.endpoints.getAuthToken.matchFulfilled,

    effect: async (action, listenerApi) => {
      const state = listenerApi.getState() as AppState

      const claimRecordId = action.payload.claimRecordId
      const isLoginEvent = state.connector.isLoginEvent
      const listenerList = state.insuranceMember.syncInsuranceListeners

      if (!isLoginEvent || !claimRecordId || !listenerList?.listeners?.length) {
        // reset login event if other conditions are not met
        isLoginEvent && listenerApi.dispatch(connectorResetOnLoginEvent())
        return
      }

      const fetchClaimRecord = async () => {
        try {
          return await listenerApi
            .dispatch(claimRecordsApi.endpoints.getClaimRecordNoCache.initiate(claimRecordId))
            .unwrap()
        } catch (error) {
          if (isGatewayTimeout(error)) {
            return await listenerApi
              .dispatch(claimRecordsApi.endpoints.getClaimRecordNoCache.initiate(claimRecordId))
              .unwrap()
          }
          throw error
        }
      }

      try {
        const claimRecord = await fetchClaimRecord()
        const { toggleOn } = getInsuranceToggleState({ claimRecord })
        invokeListeners(listenerList, toggleOn ? 'sync' : 'remove')
      } catch (error) {
        console.warn('Error invoking sync insurance listener', error)
      } finally {
        listenerApi.dispatch(connectorResetOnLoginEvent())
      }
    },
  })
}

const invokeListeners = (listenerList: SyncInsuranceListenerList, action: SyncInsuranceAction) => {
  const listeners = listenerList?.listeners ?? []

  listeners.length && console.debug('Invoking %s sync insurance listener(s) on login', listeners.length)

  try {
    listeners.forEach(handler => typeof handler === 'function' && handler(action))
  } catch (error) {
    console.warn('Error invoking sync insurance listener', error)
  }
}
